import { Inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { WINDOW_INJECTION_TOKEN } from '@core/constants/injection-tokens';
import { LoggerService } from './logger.service';
import { LogEvent } from '../enums/log-event';
import { LivechatChildEvent } from '../enums/livechat-child-event';

export const LIVECHAT_EVENT_PREFIX = 'livechat_event';

const LOCALHOST_HOSTNAMES = ['localhost', '127.0.0.1'];

/**
 * Standard channel for communication coming from child apps (the Zoid iframes
 * under `environment.appsUrl`). Child apps send a browser `message` event whose
 * `data` is a string with the shape:
 *
 *   livechat_event:<event_name>:<metadata_json_string>
 *
 * - `livechat_event` is the fixed prefix this service uses to claim the message.
 * - `<event_name>` maps to a dedicated handler.
 * - `<metadata_json_string>` is an optional stringified JSON payload.
 */
@Injectable({
  providedIn: 'root',
})
export class ChildAppMessageService {
  private listener?: (event: MessageEvent) => void;
  private allowedOrigins?: Set<string>;

  constructor(
    @Inject(WINDOW_INJECTION_TOKEN) private window: Window,
    private loggerService: LoggerService
  ) {}

  start(): void {
    if (this.listener) {
      return;
    }

    this.listener = (event: MessageEvent) => this.handleMessage(event);
    this.window.addEventListener('message', this.listener);
  }

  stop(): void {
    if (!this.listener) {
      return;
    }

    this.window.removeEventListener('message', this.listener);
    this.listener = undefined;
  }

  private handleMessage(event: MessageEvent): void {
    if (!this.isAllowedOrigin(event.origin)) {
      return;
    }

    const data = event.data;
    if (typeof data !== 'string' || !data.startsWith(`${LIVECHAT_EVENT_PREFIX}:`)) {
      return;
    }

    const { eventName, rawMetadata } = this.parseMessage(data);
    const metadata = this.parseMetadata(eventName, rawMetadata);

    this.route(eventName, metadata);
  }

  /**
   * Splits on the first two colons only. Everything after the second colon is
   * the metadata JSON, which may itself contain colons.
   */
  private parseMessage(data: string): {
    eventName: string;
    rawMetadata?: string;
  } {
    const firstColon = data.indexOf(':');
    const secondColon = data.indexOf(':', firstColon + 1);

    if (secondColon === -1) {
      return { eventName: data.slice(firstColon + 1) };
    }

    return {
      eventName: data.slice(firstColon + 1, secondColon),
      rawMetadata: data.slice(secondColon + 1),
    };
  }

  private parseMetadata(
    eventName: string,
    rawMetadata?: string
  ): Record<string, unknown> | undefined {
    if (!rawMetadata) {
      return undefined;
    }

    try {
      return JSON.parse(rawMetadata);
    } catch {
      this.loggerService.warn(LogEvent.LIVECHAT_CHILD_EVENT_INVALID_METADATA, {
        eventName,
        rawMetadata,
      });
      return undefined;
    }
  }

  private route(
    eventName: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    metadata?: Record<string, unknown>
  ): void {
    switch (eventName) {
      case LivechatChildEvent.REDIRECT_TO_PLATFORM:
        this.handleRedirectToPlatform();
        break;
      default:
        this.loggerService.warn(LogEvent.LIVECHAT_CHILD_EVENT_UNKNOWN, {
          eventName, metadata
        });
    }
  }

  private handleRedirectToPlatform(): void {
    this.window.open(environment.externalUrls.platform, '_self');
  }

  private isAllowedOrigin(origin: string): boolean {
    return this.getAllowedOrigins().has(origin);
  }

  private isLocalhostOrigin(origin: string): boolean {
    try {
      return LOCALHOST_HOSTNAMES.includes(new URL(origin).hostname);
    } catch {
      return false;
    }
  }

  private getAllowedOrigins(): Set<string> {
    if (this.allowedOrigins) {
      return this.allowedOrigins;
    }

    const urls = [
      environment.externalUrls.platform,
      environment.app.url,
      ...Object.values(environment.appsUrl),
    ];

    this.allowedOrigins = new Set(
      urls
        .map((url) => this.toOrigin(url))
        .filter((origin): origin is string => !!origin)
    );

    return this.allowedOrigins;
  }

  private toOrigin(url: string): string | undefined {
    try {
      return new URL(url).origin;
    } catch {
      return undefined;
    }
  }
}
